import { ReactNode, useState } from 'react';

import { AppStatus, loaders } from 'entities';

import { Column, Row, Loader, Button } from 'components/Blocks';
import { RefreshIcon, WarningIcon } from 'components/Icons';

import { useAuth } from 'providers/Auth';

import { createWebStorage } from 'utils/storage';

interface MaintenanceGuardProps {
  children?: never;
  app: ReactNode;
}

type AppBlock = 'VERSION' | 'MAINTENANCE' | null;

const VERSION = '40';

const FIVE_MINUTES = 1000 * 60 * 5;

const { appBlock } = createWebStorage<{ appBlock: AppBlock }>().init({
  appBlock: 'app_block',
});

export function MaintenanceGuard({ app }: MaintenanceGuardProps): JSX.Element {
  const { signOut, loginAction } = useAuth();

  const [block, setBlock] = useState<AppBlock>(() => appBlock.get() ?? null);

  function handleBlockUpdate({ logout, maintenance, version }: AppStatus): void {
    // eslint-disable-next-line prettier/prettier
    const newBlock: AppBlock = maintenance
      ? 'MAINTENANCE'
      : version.enforceUpdate
        ? 'VERSION'
        : null;

    setBlock(newBlock);

    newBlock ? appBlock.set(newBlock) : appBlock.delete();

    if (logout && !loginAction)
      signOut({ errorMessage: 'Security updated executed. You were logged out' });
  }

  const { retry, loading } = loaders.infra.appStatus.use({
    defaultValue: {
      maintenance: false,
      logout: false,
      version: { enforceUpdate: false, logout: false },
    },

    params: {
      version: VERSION,
    },

    refreshMinutes: FIVE_MINUTES,

    onLoad: handleBlockUpdate,
  });

  if (!block) return <>{app}</>;

  return (
    <Row
      alignItems="center"
      justifyContent="center"
      className="h-[100vh] bg-primary px-5 phone:px-2.5"
    >
      <Column
        gap="3"
        alignItems="center"
        className="w-full max-w-[max(25%,450px)] rounded-lg bg-primary-foreground p-8 text-center phone:p-6"
      >
        {block === 'MAINTENANCE' ? (
          <>
            {loading ? (
              <Loader type="dualRing" size={64} />
            ) : (
              <WarningIcon size={64} color="primary" />
            )}

            <h2>Maintenance underway!</h2>

            <p>OW ops is currently under maintenance. Check back at a later time</p>

            <Button onClick={() => retry()}>Retry</Button>
          </>
        ) : (
          <>
            <RefreshIcon size={64} color="primary" />

            <h2>Version updated needed</h2>

            <p>
              We&apos;ve updated our version. Please <strong>refresh</strong> the page to access the
              current version
            </p>

            <Button onClick={() => location.reload()}>Refresh</Button>
          </>
        )}
      </Column>
    </Row>
  );
}
